import { useContext } from "react";
import { useParams } from "react-router-dom";

import { UserContext } from "@/app/providers";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { CompetenceStatusUpdate } from "@/features/competency/model/competence/UserCompetence";
import { ChecklistQueryKeys } from "@/features/procedures/api/checklist.queries";
import { NodeQueryKeys } from "@/features/procedures/api/nodes.queries";
import {
	ProcedureQueryKeys,
	ReorderAccordion,
} from "@/features/procedures/api/procedure.queries";
import { EditProcedure } from "@/features/procedures/model/procedure/EditProcedure";
import { ProcedureDetails } from "@/features/procedures/model/procedure/ProcedureDetail";
import { CreateNodeRequest } from "@/features/procedures/model/procedure/ProcedureNodeDependency";
import { UpdateProcedureNodeRequest } from "@/features/procedures/model/procedure/UpdateProcedureNodeRequest";
import { type CreateImportedChecklistRequest, DocumentImportPlan, UpdateImportedChecklistRequest } from "@/features/procedures/model/types/checklist";
import { CreateNewProcedureRequest } from "@/features/procedures/model/types/createNewProcedure";
import { DocumentUpdateRequest } from "@/features/procedures/model/types/procedure";
import {
	DeleteProcedureNodeRequest,
	ProcedureGroup,
} from "@/features/procedures/model/types/procedure";
import { SaveProcedureRequest } from "@/features/procedures/model/types/procedure";
import { ProcedureAccordionMove } from "@/features/procedures/model/types/procedureAccordionMove";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useUpdateNodeTreePosition = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ProcedureAccordionMove) =>
			SATOSPortal.UpdateNodeTreePosition(request),
		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error

		onMutate: (request: ProcedureAccordionMove) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, request.ProcedureId],
			});

			const previousValue: ProcedureDetails | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
				]);

			const newValue = ReorderAccordion(
				previousValue,
				request.Node,
				request.Direction,
			);

			if (newValue) {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureDetails, request.ProcedureId],
					newValue,
				);
			}

			return { previousValue, newValue };
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (procedureId) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, procedureId],
			});
		},
	});

	return mutation.mutateAsync;
};

/**
 * Persisted tree move without optimistic cache transforms.
 * Useful for multi-step reorder operations (e.g. drag-and-drop) where
 * repeated optimistic "single step" updates can drift from server state.
 */
export const useMoveNodeTreePosition = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ProcedureAccordionMove) =>
			SATOSPortal.UpdateNodeTreePosition(request),
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useMutateDeleteProcedure = () => {
	const queryClient = useQueryClient();
	const user = useContext(UserContext);
	const companyId = user?.CompanyId;

	const mutation = useMutation({
		mutationFn: (request: EditProcedure) =>
			SATOSPortal.DeleteProcedure({ procedureId: request.ProcedureId }),

		onMutate: (request: EditProcedure) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureGroup, request.ProjectGroup],
			});

			queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
			});
			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedures,
				ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureGroups,
					companyId,
					ProcedureQueryKeys.orderByNameAsc,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
			});
		},
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						"procedure",
						variables.procedureId,
						{ refetchActive: true, refetchInactive: true },
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.groupdetails,
						variables.ParentId,
						companyId,
					],
				});
			}

			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.groupdetails],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedure],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureGroups,
					companyId,
					ProcedureQueryKeys.orderByNameAsc,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useMutateUpdateProcedureSettings = () => {
	const queryClient = useQueryClient();

	const user = useContext(UserContext);
	const { mutateAsync: updateProcedure, isPending: isSubmitting } = useMutation(
		{
			mutationFn: (request: EditProcedure) =>
				SATOSPortal.UpdateProcedureSetting(request),

			// Optimistically update the cache value on mutate, but store
			// the old value and return it so that it's accessible in case of
			// an error
			onMutate: (request: EditProcedure) => {
				queryClient.cancelQueries({
					queryKey: [ProcedureQueryKeys.procedure, request.ProcedureId],
				});
				const previousValue: EditProcedure | undefined =
					queryClient.getQueryData([
						ProcedureQueryKeys.procedure,
						request.ProcedureId,
					]);

				return previousValue;
			},
			// On failure, roll back to the previous value
			onError: (err, variables, previousValue) => {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedure, variables.ProcedureId],
					previousValue,
				);

				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedure, variables.ProcedureId],
				});
			},
			// After success or failure, refetch the todos query
			onSettled: (data, error, variables: EditProcedure) => {
				if (variables) {
					queryClient.invalidateQueries({
						queryKey: [ProcedureQueryKeys.procedure, variables.ProcedureId],
					});
				}
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedures],
				});

				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
				});

				// Invalidates the cache for the checklist page
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
			},
		},
	);
	return { updateProcedure, isSubmitting };
};

export const useMutateProcedureKnowledgeStatus = () => {
	const queryClient = useQueryClient();
	const { id } = useParams();

	const mutation = useMutation({
		mutationFn: (request: CompetenceStatusUpdate) => {
			return SATOSPortal.PostProcedureKnowledgeStatus(request);
		},
		onMutate: (request) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge, request.ProcedureId],
			});

			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedureKnowledge,
				request.ProcedureId,
			]);

			return { previousValues };
		},
		onError: (err, variables, context) => {
			if (context?.previousValues) {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureKnowledge, variables.ProcedureId],
					context.previousValues,
				);
			}
		},
		onSettled: (variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge, id],
			});
		},
	});

	return mutation.mutateAsync;
};

export const SaveAndResetProcedure = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: SaveProcedureRequest) =>
			SATOSPortal.SaveAndResetProcedure(request),
		onMutate: (request: SaveProcedureRequest) => {
			const { ProcedureId } = request;
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, ProcedureId],
			});
			const previousValues: ProcedureDetails | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureDetails,
					ProcedureId,
				]);

			return previousValues;
		},
		onError: (err, variables, previousValues) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
				previousValues,
			);
		},
		onSettled: async (_data, _error, variables) => {
			// Refresh both the procedure execution view and the browse/active lists
			// so completion is reflected immediately after the modal closes.
			const refreshTargets = [
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedure, variables?.ProcedureId],
				}),
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedureDetails],
				}),
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedures],
				}),
				queryClient.invalidateQueries({
					queryKey: [ChecklistQueryKeys.checklist],
				}),
			];

			await Promise.all(refreshTargets);
		},
	});
	return mutation.mutateAsync;
};

export const useMutateReimportDocument = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DocumentUpdateRequest | undefined) =>
			SATOSPortal.ReimportDocument(request),
		onSettled: (data, error, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.document],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables?.ProcedureId],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useUpdateProcedureGroup = () => {
	const user = useContext(UserContext);
	const companyId = user?.CompanyId;
	const queryClient = useQueryClient();

	const { mutateAsync: updateProcedureFolder, isPending: isSubmitting } =
		useMutation({
			mutationFn: (request: ProcedureGroup) =>
				SATOSPortal.UpdateProcedureFolder(request),
			onMutate: (request: ProcedureGroup) => {
				queryClient.cancelQueries({
					queryKey: [ProcedureQueryKeys.procedureGroup, request.Id],
				});
				queryClient.setQueryData(
					[ProcedureQueryKeys.groupdetails, request.Id, companyId],
					request,
				);
			},
			onError: (err, variables, previousValue) => {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureGroup, variables.Id],
					previousValue,
				);
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureGroups,
						companyId,
						ProcedureQueryKeys.orderByNameAsc,
					],
				});
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureGroups,
						companyId,
						ProcedureQueryKeys.orderByNameAsc,
					],
				});

				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
				});

				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.metaData],
				});

				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedureMetaData],
				});
			},
		});

	return { updateProcedureFolder, isSubmitting };
};

export const SaveProcedureGroup = () => {
	const user = useContext(UserContext);
	const companyId = user?.CompanyId;

	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ProcedureGroup) =>
			SATOSPortal.UpdateProcedureGroup(request),
		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error
		onMutate: (request: ProcedureGroup) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureGroup, request.Id],
			});
			const previousValue: ProcedureGroup | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureGroup,
					request.Id,
				]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureGroup, variables.Id],
				previousValue,
			);

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureGroups,
					companyId,
					ProcedureQueryKeys.orderByNameAsc,
				],
			});
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureGroup,
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
			}

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureGroups,
					companyId,
					ProcedureQueryKeys.orderByNameAsc,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.metaData],
			});

			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureMetaData],
			});
		},
	});
	return mutation.mutateAsync;
};

export const DeleteProcedureNode = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DeleteProcedureNodeRequest) =>
			SATOSPortal.DeleteProcedureNode(request),
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.document,
						ProcedureQueryKeys.node,
						variables.SchemeId,
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const UpdateProcedureNode = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: UpdateProcedureNodeRequest) =>
			SATOSPortal.UpdateProcedureNode(request),
		onMutate: (request: UpdateProcedureNodeRequest) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, request.ProcedureId],
			});
			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedureDetails,
				request.ProcedureId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
		},
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.document,
						ProcedureQueryKeys.node,
						variables.SchemeId,
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const useMutateCreateNewProcedure = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createProcedure, isPending: isSubmitting } = useMutation(
		{
			mutationFn: (request: CreateNewProcedureRequest) =>
				SATOSPortal.CreateProcedure(request),
			onMutate: () => {
				queryClient.cancelQueries({
					queryKey: [
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
				});
				const previousValues = queryClient.getQueryData([
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				]);

				return previousValues;
			},
			onError: (_, variables, previousValues) => {
				queryClient.setQueryData(
					[
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
					previousValues,
				);

				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
				});
			},
			onSettled: (variables) => {
				if (variables) {
					queryClient.invalidateQueries({
						queryKey: [
							ProcedureQueryKeys.procedures,
							ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
						],
					});
				}
			},
		},
	);

	return { createProcedure, isSubmitting };
};

export const useCreateNode = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (request: CreateNodeRequest) => {
			const response = await SATOSPortal.CreateNode(request);
			if (!response?.SchemeId) {
				throw new Error("Failed to create node.");
			}
			return response;
		},
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
		},
	}).mutateAsync;
};

export const useCreateImportedChecklist = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (request: CreateImportedChecklistRequest) =>
			SATOSPortal.CreateImportedChecklist(request),
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
		},
	}).mutateAsync;
};

export const useUpdateImportedChecklist = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: UpdateImportedChecklistRequest) =>
			SATOSPortal.UpdateImportedChecklist(request),
		onSettled: (_data, _error, variables) => {
			if (!variables) return;

			queryClient.invalidateQueries({
				queryKey: [
					NodeQueryKeys.nodes,
					variables.NodeId,
					NodeQueryKeys.checklists,
					variables.SchemeId,
					NodeQueryKeys.importPlan,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					variables.ProcedureId,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, variables.SchemeId],
			});
		},
	});

	return mutation.mutateAsync;
};

export const ImportPlanFromWizard = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DocumentImportPlan | undefined) =>
			SATOSPortal.ImportPlanFromWizard(request),
		onSettled: (_data, _error, variables) => {
			if (!variables) return;

			queryClient.invalidateQueries({
				queryKey: [
					NodeQueryKeys.nodes,
					variables.NodeId,
					NodeQueryKeys.checklists,
					variables.SchemeId,
					NodeQueryKeys.importPlan,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					variables.ProcedureId,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, variables.SchemeId],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useConvertToManualChecklist = () => {
	const mutation = useMutation({
		mutationFn: (_request: { SchemeId: number; ProcedureId: string }) =>
			Promise.reject(new Error("Convert to manual checklist is currently disabled.")),
	});
	return mutation.mutateAsync;
};
